import { localeText } from '../locale';
import { photoByKey, publicPhotoPath } from '../renderers/archive-act';
import type { PhotoSpec } from './primitives';
import type { StoriesGroup, StoriesViewer } from './stories-viewer';

interface ContextualStoriesOptions {
  nav: HTMLElement;
  groups: StoriesGroup[];
  viewer: StoriesViewer;
}

function previewImage(photo: PhotoSpec): HTMLImageElement | null {
  const record = photoByKey.get(photo.key);
  const variant = record?.v.find(({ width }) => width >= 480) ?? record?.v[0];
  if (!variant) return null;

  const image = document.createElement('img');
  image.src = publicPhotoPath(variant.src);
  image.width = variant.width;
  image.height = variant.height;
  image.alt = '';
  image.decoding = 'async';
  return image;
}

export function mountContextualStories({
  nav,
  groups,
  viewer,
}: ContextualStoriesOptions): HTMLButtonElement | null {
  const bar = nav.querySelector<HTMLElement>('.ep-segments__bar');
  const menu = bar?.querySelector<HTMLElement>('.ep-segments__menu-trigger');
  if (!bar || !menu || groups.length === 0) return null;

  const groupsById = new Map(groups.map((group, index) => [group.id, { group, index }]));
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'ep-segments__stories';
  button.hidden = true;

  const stack = document.createElement('span');
  stack.className = 'ep-segments__stories-stack';
  stack.setAttribute('aria-hidden', 'true');

  const copy = document.createElement('span');
  copy.className = 'ep-segments__stories-copy';
  const kicker = document.createElement('small');
  kicker.textContent = localeText('Эта глава', 'Бул бөлүм');
  const label = document.createElement('strong');
  copy.append(kicker, label);
  button.append(copy, stack);
  bar.insertBefore(button, menu);
  nav.classList.add('has-contextual-stories');

  let currentGroupIndex = -1;
  let lastRenderKey = '';

  const render = (): void => {
    const chapterIndex = nav.dataset.activeIndex ?? '';
    const context = groupsById.get(chapterIndex);
    if (!context) {
      button.hidden = true;
      currentGroupIndex = -1;
      lastRenderKey = '';
      return;
    }

    const renderKey = `${context.group.id}:${context.group.slides.map((slide) => slide.photo?.key ?? `${slide.format}:${slide.title ?? ''}`).join(',')}`;
    if (renderKey === lastRenderKey) return;
    lastRenderKey = renderKey;
    currentGroupIndex = context.index;
    button.hidden = false;
    label.textContent = localeText(
      `${context.group.slides.length} историй`,
      `${context.group.slides.length} баян`,
    );
    button.setAttribute(
      'aria-label',
      localeText(
        `Смотреть главу ${chapterIndex} «${context.group.title}» как сторис`,
        `${chapterIndex}-бөлүмдү «${context.group.title}» сторис форматында көрүү`,
      ),
    );

    stack.replaceChildren();
    const previewSlides = context.group.slides.filter((slide) => slide.photo).slice(0, 3);
    for (const [previewIndex, slide] of previewSlides.entries()) {
      const frame = document.createElement('span');
      frame.className = 'ep-segments__stories-thumb';
      frame.style.setProperty('--ep-story-thumb-index', String(previewIndex));
      if (slide.photo) {
        const image = previewImage(slide.photo);
        if (image) frame.append(image);
      }
      stack.append(frame);
    }
  };

  button.addEventListener('click', () => {
    if (currentGroupIndex < 0) return;
    viewer.show({
      startGroupIndex: currentGroupIndex,
      returnFocusTo: button,
    });
  });

  const observer = new MutationObserver(render);
  observer.observe(nav, {
    attributes: true,
    attributeFilter: ['data-active-index'],
  });
  render();

  return button;
}
